import { useId } from 'react'
import type { JSX } from 'react'

// Tile size of the diagonal-line pattern. Larger = lines further apart
// (perpendicular spacing ≈ tile / √2 at 45°).
const HATCH_TILE_SIZE = 24
const HATCH_STROKE_WIDTH = 1
const HATCH_STROKE_OPACITY = 0.25
// How far the corner segments overhang the tile so the diagonal tiles seamlessly.
const HATCH_CORNER_OVERLAP = 2

interface NoDataHatchProps {
  /** Stroke colour for the diagonal lines. */
  color?: string
  /** Applied to the root `<g>` — e.g. a reveal animation when the flag opens. */
  className?: string
  // Rect geometry injected by Recharts when this is used as a `ReferenceArea`
  // `shape` — the pixel bounds of the reference region.
  x?: number
  y?: number
  width?: number
  height?: number
}

/**
 * Diagonal-line hatch fill marking a "no data" region, used as the `shape` of a
 * Recharts `ReferenceArea`. Recharts injects the region's `x`/`y`/`width`/
 * `height`; this renders a self-contained SVG `<pattern>` and fills a `<rect>`
 * with it, so a caller only has to decide the region's bounds (the `x1`/`x2` of
 * the `ReferenceArea`).
 */
export function NoDataHatch({
  color,
  className,
  x,
  y,
  width,
  height,
}: NoDataHatchProps): JSX.Element | null {
  // `useId` output contains colons; strip them for a valid `url(#…)` reference.
  const patternId = `no-data-hatch-${useId().replace(/:/g, '')}`

  if (x == null || y == null || width == null || height == null || width <= 0) {
    return null
  }

  const tile = HATCH_TILE_SIZE
  const overlap = HATCH_CORNER_OVERLAP
  // Main diagonal plus two corner segments so adjacent tiles join seamlessly.
  const linePath =
    `M0,${tile} L${tile},0 ` +
    `M-${overlap},${overlap} L${overlap},-${overlap} ` +
    `M${tile - overlap},${tile + overlap} L${tile + overlap},${tile - overlap}`

  return (
    <g className={className}>
      <defs>
        <pattern
          id={patternId}
          patternUnits="userSpaceOnUse"
          width={tile}
          height={tile}
        >
          <path
            d={linePath}
            stroke={color}
            strokeWidth={HATCH_STROKE_WIDTH}
            strokeOpacity={HATCH_STROKE_OPACITY}
          />
        </pattern>
      </defs>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={`url(#${patternId})`}
      />
    </g>
  )
}
